import { CheckIcon } from '@heroicons/vue/outline';
import Spinner from '@/components/ui/spinner.component.vue';
import {
  FormField,
  getMinimumDateForCreationNewPerson,
  getInvalidFields,
  getToday,
  toTitleCase,
  ADDRESS_FIELD_MAX_LENGTH,
} from '@/shared/utils';
import { ComponentPublicInstance } from 'vue';
import { Options, Vue } from 'vue-class-component';
import Modal from '@/components/modal/Modal.vue';
import HtmlTextarea from '@/components/ui/html-textarea.component.vue';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';
import ErrorDisplay from '@/components/ui/error-display.component.vue';
import {
  ApiGenderModel,
  ConfigWebUserModel,
  ErrorModel,
  GenderNameValueModel,
  GENDERS,
  LanguageModel,
  ONLY_DEFINED_GENDERS,
  PersonDetailModel,
  SalutationModel,
  MemberFormConfigModel,
  VIEW_NOT_USED_OPTIONAL_REQUIRED,
} from '../../../../common/src';
import { Watch, Inject } from 'vue-property-decorator';
import store from '@/store';
import PersonService from '@/services/person.service';
import { formatDateToUTC } from '@/shared/date-utils';
import { familyMemberPattern } from '@/shared/route-utils';
import { getZipCodeFormat } from '@/shared/utils';

const stateList = require.context('@/assets')('./state-list.json');

class FormFields {
  constructor(
    public subscriptionStartDate: FormField = {},
    public email: FormField = {},
    public confirmEmail: FormField = {},
    public password: FormField = {},
    public confirmPassword: FormField = {},
    public salutation: FormField = {},
    public gender: FormField = {},
    public firstName: FormField = {},
    public lastName: FormField = {},
    public birthday: FormField = {},
    public phone: FormField = {},
    public idType: FormField = {},
    public idNumber: FormField = {},
    public nationalId: FormField = {},
    public residentId: FormField = {},
    public passport: FormField = {},
    public passportDate: FormField = {},
    public passportCountry: FormField = {},
    public address1: FormField = {},
    public address2: FormField = {},
    public zipCode: FormField = {},
    public city: FormField = {},
    public country: FormField = {},
    public state: FormField = { options: stateList },
    public ssn: FormField = {},
    public terms: FormField = { value: false },
    public checkbox1: FormField = { value: false },
    public checkbox2: FormField = { value: false },
    public checkbox3: FormField = { value: false },
    public language: FormField = {},
    public allowThirdPartyOffers: FormField = { value: false },
    public allowEmailNewsletters: FormField = { value: false }
  ) {}
}

export interface VueTelInputModel {
  countryCallingCode: string;
  nationalNumber: string;
  number: string;
  country: Country;
  countryCode: string;
  valid: boolean;
  formatted: string;
}

export interface Country {
  name: string;
  iso2: string;
  dialCode: string;
}

@Options({
  props: {
    formConfig: Object,
    mode: String,
    showStartupDate: Boolean,
    selectedCenter: Object,
  },
  emits: [],
  components: {
    Spinner,
    CheckIcon,
    Modal,
    HtmlTextarea,
    VueTelInput,
    ErrorDisplay,
  },
})
export default class MemberForm extends Vue {
  @Inject('personService')
  public personService!: PersonService;

  // TODO DEV-47286 Double check if this gets picked up even when only one attribute is changed
  formConfig!: MemberFormConfigModel;
  getZipCodeFormatForCountry = getZipCodeFormat;

  vueTelProps = {
    mode: 'international',
    inputOptions: {
      showDialCode: true,
      placeholder: '',
    },
    disabledFormatting: false,
    wrapperClasses: 'country-phone-input',
    autoFormat: true,
    defaultCountry: '',
    validCharactersOnly: true,
  };

  phoneTelInfo: VueTelInputModel | null = null;
  salutations: SalutationModel[] = [];
  languages: LanguageModel[] = [];
  genders: GenderNameValueModel[] | null = null;

  showGender = false;

  formElements: FormFields = new FormFields();
  minClubStartupDate: string | undefined = undefined;
  minSubStartupDate: string | undefined = undefined;
  minStartupDate: string | undefined = undefined;
  fixedStartupDate = false;

  public globalError: ErrorModel | null = null;
  public brazeError: ErrorModel | null = null;
  loadingPreferenceCenter = false;
  loadingForm = true;

  vueTelPhone? = '';
  cprIsUsed = false;

  // add subscption for a family member
  addChildSubscription = false;

  allAttributesAreHidden = true;

  // watch for changes in the config object
  @Watch('formConfig', { deep: true, immediate: true })
  onFormConfigChanged(newConfig: MemberFormConfigModel, oldConfig: MemberFormConfigModel) {
    // TODO DEV-47286 This normally should only be called once and not everytime the config changes
    // Let's see what I can do about it later
    if (!newConfig) {
      return;
    }
    this.loadingForm = true;

    if (this.formConfig!.mode === 'LEAD') {
      // TODO: get this not from the store, but from the API call
      if (this.formConfig!.leadType === 'StandAlone') {
        this.genders = store.state.config!.standaloneFlow.hideUndefinedGender ? ONLY_DEFINED_GENDERS : GENDERS;
      } else {
        this.genders = store.state.config!.webLeadFlow.hideUndefinedGender ? ONLY_DEFINED_GENDERS : GENDERS;
      }
      this.initCentersCountry(); // DEV-47286 should only be called when there is a summary and it is not a lead
    } else if (familyMemberPattern.test(this.$route.path)) {
      this.genders = GENDERS;
      this.addChildSubscription = true;
    } else {
      this.genders = store.state.config!.joinFlow.hideUndefinedGender ? ONLY_DEFINED_GENDERS : GENDERS;
      this.initCentersCountry(); // DEV-47286 should only be called when there is a summary and it is not a lead
    }
    // for some reason, this.$t is not available inside the promise below
    const translation = this.$t('general.placeHolders.yourPhone');
    this.updateLocaleInformation().then(() => {
      this.vueTelProps.inputOptions.placeholder = translation;
      // TODO DEV-47286 This can be done here instead of my-personal-info component
      // this.allAttributesAreHidden = this.checkFormState(this.formConfig.systemPropertyConfig);
      // TODO DEV-47286 Improve this so that it works for both lead, join and profile
      // In the profile, showGender will only show if the salutation is selected and there is an undefined gender

      // switch cases for this.formConfig.mode
      // LEAD: showGender = false
      // JOIN: showGender = true
      // PROFILE: showGender = true
      switch (this.formConfig!.mode) {
        case 'LEAD':
        case 'JOIN':
          this.showGender =
            this.formConfig!.systemPropertyConfig.salutation === 'not-used' &&
            (!this.formConfig!.systemPropertyConfig.genderDefaultValue ||
              this.formConfig!.systemPropertyConfig.genderDefaultValue === 'none');
          break;
        case 'PROFILE':
          this.showGender =
            this.formConfig!.systemPropertyConfig.salutation === 'not-used' &&
            (this.formConfig!.systemPropertyConfig.gender === 'view' || this.formConfig!.systemPropertyConfig.gender === 'required');
          break;
        case 'ADD':
        case 'CHANGE':
          this.showGender = false; // It will depend exclusively on the salutation. You can only change the gender, when you change your salutation and we cannot infere the corresponding gender
          break;
      }

      if (this.addChildSubscription) {
        this.showGender = true;
      }

      switch (newConfig.prefillValues) {
        case 'ALL':
          this.prefillUserValues(newConfig.personDetail!, newConfig.systemPropertyConfig);
          break;
        case 'DEFAULT':
          this.prefillDefaultValues(newConfig.systemPropertyConfig);
          break;
      }
    });
  }

  // Notify GTM when the member form is ready for the Loqate address lookup integration
  @Watch('loadingForm')
  onLoadingFormChanged(isLoading: boolean) {
    /* istanbul ignore next */
    if (isLoading) {
      return;
    }
    this.applyPhoneInputDirection();

    const isEditableProfile = this.formConfig && this.formConfig.mode === 'PROFILE' && this.formConfig.viewMode === 'EDIT';
    const shouldSendReadyEvent = this.formConfig && (this.formConfig.mode !== 'PROFILE' || isEditableProfile);
    if (!shouldSendReadyEvent) {
      return;
    }
    this.$nextTick(() => {
      /* istanbul ignore next */
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'member_form_ready',
          form_location: 'member_form',
        });
      }
    });
  }

  @Watch('$store.state.currentLocale.locale', { immediate: true, deep: true })
  async updateLocaleInformation() {
    // TODO DEV-47286 we should wait for both country list and locale parameters
    this.personService.getCountriesList().then(result => {
      this.formElements.country.options = result;
      this.formElements.passportCountry.options = result;
    });
    const centerId: number | undefined = this.formConfig?.selectedCenter?.id || store.state.user?.centerId;
    if (centerId && this.formConfig) {
      return this.loadParams(centerId);
    }
    this.loadingForm = false;
  }

  @Watch('$store.state.currentLocale.direction', { immediate: true })
  onLocaleDirectionChanged() {
    this.applyPhoneInputDirection();
  }

  async loadParams(centerId: number) {
    const withLanguages = this.formConfig.systemPropertyConfig.preferredLanguage !== 'not-used';
    const withSalutations = this.formConfig.systemPropertyConfig.salutation !== 'not-used';
    // TODO DEV-47286 show an error message when there is no salutation or no language
    return this.personService
      .getLocaleParameters(centerId, withSalutations, withLanguages)
      .then(result => {
        this.salutations = result.salutations;
        this.languages = result.languages;
      })
      .catch((e: ErrorModel) => {
        this.globalError = e;
        this.loadingForm = false;
      });
  }

  get isStandAloneLeadFlow(): boolean {
    return this.$route.name === 'LeadGeneration-MemberDetails';
  }

  mounted() {}

  prefillDefaultValues(config: ConfigWebUserModel) {
    if (!this.formElements.salutation.value) {
      this.formElements.salutation.value = this.salutations.find(
        s => (s.value + '').toLowerCase() === (config.salutationDefaultValue + '').toLowerCase()
      );
    }
    if (this.formElements.salutation.value) {
      this.checkInput('salutation', true);
    }
    if (!this.formElements.gender.value) {
      this.formElements.gender.value = GENDERS.find(
        s => (s.name + '').toLowerCase() === (config.genderDefaultValue + '').toLowerCase()
      )?.value;
    }
    if (!this.formElements.country.value) {
      this.formElements.country.value = config.countryDefaultValue;
    }
    if (!this.formElements.zipCode.value) {
      this.formElements.zipCode.value = config.zipDefaultValue;
    }
    if (this.formElements.zipCode.value) {
      this.checkCities(true, this.formElements.city.value || config.cityDefaultValue);
    }
    if (!this.formElements.state.value) {
      this.formElements.state.value = config.stateDefaultValue;
    }
    this.loadingForm = false;
  }

  prefillUserValues(result: PersonDetailModel, config: ConfigWebUserModel) {
    for (const key in this.formElements) {
      (this.formElements as any)[key].error = '';
    }
    this.formElements.gender.value = result.gender;

    if (!this.showGender) {
      // TODO DEV-47286 Double check this logic
      this.formElements.salutation.value = this.salutations.find(s => s.value === result.salutation);
    }

    this.formElements.language.value = result.preferredLanguage;
    this.formElements.firstName.value = result.firstName;
    this.formElements.lastName.value = result.lastName;
    this.formElements.birthday.value = result.birthday;
    this.formElements.phone.value = result.mobile;
    this.vueTelPhone = result.mobile;
    this.formElements.country.value = result.country;
    this.formElements.address1.value = result.address1;
    this.formElements.address2.value = result.address2;

    this.formElements.state.value = result.state;

    this.formElements.zipCode.value = result.zip;
    this.checkCities(true, result.city);
    // this.formElements.city.value = result.city;
    this.formElements.ssn.value = result.ssn;

    this.formElements.allowEmailNewsletters.value = result.allowEmailNewsletters;
    this.formElements.allowThirdPartyOffers.value = result.allowThirdPartyOffers;

    // reset the terms and conditions and stuff
    this.formElements.terms.value = false;
    this.formElements.checkbox1.value = false;
    this.formElements.checkbox2.value = false;
    this.formElements.checkbox3.value = false;

    // TODO 49232 Since this is only called when there is an existing user, we will always use the separated ids fields
    this.formElements.nationalId.value = result.nationalId;
    this.formElements.residentId.value = result.residentId;
    this.formElements.passport.value = result.passportNumber;
    this.formElements.passportDate.value = result.passportExpiration;
    this.formElements.passportCountry.value = result.passportIssuingCountry;
    this.loadingForm = false;
  }

  checkAddressField(field: 'address1' | 'address2') {
    const element = (this.formElements as any)[field];
    if (element.value && element.value.length > ADDRESS_FIELD_MAX_LENGTH) {
      element.error = this.$t('subscription.addressMaxLengthError', { maxCharLimit: ADDRESS_FIELD_MAX_LENGTH });
    } else {
      element.error = '';
    }
  }

  checkInput(name: string, check = true) {
    const todaysDate = new Date();
    todaysDate.setHours(0, 0, 0, 0);
    const element = (this.formElements as any)[name];
    this.globalError = null;

    if (typeof element.value === 'string') {
      element.value = element.value.trim();
    }
    if ((name === 'idNumber' && this.formElements.idType.value === 'nationalId') || name === 'nationalId') {
      element.value = this.formatNationalId(element.value);
    }

    switch (name) {
      case 'idType':
        element.error = '';
        if (this.formConfig.mode === 'JOIN' || this.formConfig.mode === 'LEAD') {
          if (!element.value && store.state.config!.features.userIdRequired && !this.isStandAloneLeadFlow && this.isAnyIdFieldEnabled()) {
            element.error = 'subscription.selectIdType';
          }
          if (element.value !== 'passport') {
            this.formElements.passportCountry.value = undefined;
            this.formElements.passportCountry.error = undefined;
            this.formElements.passportDate.value = undefined;
            this.formElements.passportDate.error = undefined;
          }
        }
        this.checkInput('idNumber');
        break;
      case 'idNumber':
        element.error = '';
        if (this.formConfig.mode === 'JOIN' || this.formConfig.mode === 'LEAD') {
          if (element.value) {
            element.value = element.value.toUpperCase();
          }
          if (!element.value && store.state.config!.features.userIdRequired && !this.isStandAloneLeadFlow && this.isAnyIdFieldEnabled()) {
            element.error = 'subscription.selectIdNumber';
          }
        }
        break;
      case 'nationalId':
      case 'residentId':
      case 'passport':
        element.error = '';
        if (element.value) {
          element.value = element.value.toUpperCase();
        }
        if (this.formConfig.mode !== 'JOIN' && this.formConfig.mode !== 'LEAD' && this.checkIfuserIdIsRequired()) {
          element.error = 'subscription.selectIdNumber';
        }
        break;
      case 'passportDate':
        element.error = '';
        if (!element.value && this.formElements.idType.value === 'passport') {
          element.error = 'subscription.selectPassportDate';
        } else if (element.value && element.value < getToday()) {
          element.error = 'subscription.passportDateInvalid';
        }
        break;
      case 'passportCountry':
        element.error = '';
        if (!element.value && this.formElements.idType.value === 'passport') {
          element.error = 'subscription.selectPassportCountry';
        }
        break;
      case 'firstName':
        element.error = '';
        if (!element.value) {
          element.error = 'subscription.enterFirstname';
        } else {
          // Convert the first name to title case
          element.value = toTitleCase(element.value);
        }
        break;
      case 'lastName':
        element.error = '';
        if (!element.value) {
          element.error = 'subscription.enterLastname';
        } else {
          // Convert the last name to title case
          element.value = toTitleCase(element.value);
        }
        break;
      case 'birthday':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.birthday === 'required') {
          element.error = 'subscription.enterBirthday';
        } else if (element.value && element.value > getMinimumDateForCreationNewPerson(this.formConfig!.systemPropertyConfig)) {
          element.error = 'subscription.belowMinimumAge';
        }
        break;
      case 'phone':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.mobile === 'required') {
          element.error = 'subscription.enterPhone';
        }
        if (this.phoneTelInfo != null && element.value && !this.phoneTelInfo!.valid) {
          element.error = 'subscription.invalidPhone';
        }
        break;
      case 'language':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.preferredLanguage === 'required') {
          element.error = 'subscription.enterPreferredLanguage';
        }
        break;
      case 'address1':
        element.error = '';
        this.checkAddressField('address1');
        if (!element.value && this.formConfig!.systemPropertyConfig!.address1 === 'required') {
          element.error = 'subscription.enterAddress';
        }
        break;
      case 'address2':
        element.error = '';
        this.checkAddressField('address2');
        if (!element.value && this.formConfig!.systemPropertyConfig!.address2 === 'required') {
          element.error = 'subscription.enterAddress';
        }
        break;
      case 'ssn':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.ssn === 'required') {
          element.error = 'subscription.enterSSN';
        }
        break;
      case 'zipCode':
        this.checkCities(check, undefined);
        break;
      case 'city':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.city === 'required') {
          element.error = 'subscription.enterCity';
        }
        break;
      case 'state':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.state === 'required') {
          element.error = 'subscription.enterState';
        }
        break;
      case 'country':
        element.error = '';
        if (!element.value) {
          this.formElements.zipCode.value = undefined;
          this.formElements.zipCode.error = undefined;
          this.formElements.city.value = undefined;
          this.formElements.city.error = undefined;
          if (this.formConfig!.systemPropertyConfig!.country === 'required') {
            element.error = 'subscription.enterCountry';
          }
        } else if (check) {
          this.resetCountryRelatedFields();
        }
        break;
      case 'gender':
        element.error = '';
        if (!element.value) {
          element.error = 'subscription.enterGender';
        }
        break;
      case 'salutation':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.salutation === 'required') {
          element.error = 'subscription.enterSalutation';
        } else if (element.value && check) {
          // TODO DEV-47286 - This could be done in a better way to eliminate the need for this check the mode
          this.showGender =
            (this.formConfig?.mode === 'PROFILE' || this.formConfig?.mode === 'ADD' || this.formConfig?.mode === 'CHANGE') &&
            this.formConfig!.systemPropertyConfig!.gender !== 'not-used' &&
            element.value.gender === ApiGenderModel.Undefined;
          this.formElements.gender.value = element.value.gender;
        }
        break;
      case 'terms':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.termsAndConditions && this.formConfig!.mode !== 'PROFILE') {
          element.error = 'subscription.acceptTerms';
        }
        break;
      case 'checkbox1':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.checkbox1 && this.formConfig!.mode !== 'PROFILE') {
          element.error = 'subscription.acceptCondition';
        }
        break;
      case 'checkbox2':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.checkbox2 && this.formConfig!.mode !== 'PROFILE') {
          element.error = 'subscription.acceptCondition';
        }
        break;
      case 'checkbox3':
        element.error = '';
        if (!element.value && this.formConfig!.systemPropertyConfig!.checkbox3 && this.formConfig!.mode !== 'PROFILE') {
          element.error = 'subscription.gdprCondition';
        }
        break;
    }
  }

  redirectToPreferenceCenter() {
    this.loadingPreferenceCenter = true;
    this.brazeError = null;
    this.personService
      .openBraze()
      .then(result => {
        this.loadingPreferenceCenter = false;
        window.open(result);
      })
      .catch((e: ErrorModel) => {
        this.brazeError = e;
        this.loadingPreferenceCenter = false;
      });
  }
  private checkIfuserIdIsRequired() {
    return (
      store.state.config?.features.userIdRequired &&
      !this.formElements.nationalId.value &&
      !this.formElements.residentId.value &&
      !this.formElements.passport.value
    );
  }
  private isAnyIdFieldEnabled(): boolean {
    const config = this.formConfig.systemPropertyConfig;
    return config.passport !== 'not-used' || config.nationalId !== 'not-used' || config.residentId !== 'not-used';
  }
  private formatNationalId(value: string | null) {
    if (!value) {
      return value;
    }
    return value.match(/[a-z]+|\d+/gi)!.join('-');
  }
  checkCities(loadCities: boolean, selectedCity: string | undefined) {
    this.formElements.zipCode.error = '';
    if (!this.formElements.zipCode.value && this.formConfig!.systemPropertyConfig!.zip === 'required') {
      this.formElements.zipCode.error = 'subscription.enterZipcode';
    } else if (loadCities) {
      // doesn't work for US
      if (this.formElements.country.value !== 'US' && this.formElements.zipCode.value) {
        this.formElements.city.error = '';
        this.formElements.city.checking = true;
        this.formElements.city.options = [];
        this.personService
          .getCitiesByZipCode(this.formElements.zipCode.value, this.formElements.country.value)
          .then(cities => {
            this.formElements.city.checking = false;
            this.formElements.city.options = cities.nonTranslated.map((city, index) => ({
              original: city,
              translated: cities.translated[index],
            }));
            if (selectedCity) {
              this.formElements.city.value = this.formElements.city.options.find(c => c.original === selectedCity).original;
            } else if (this.formElements.city.options.length === 1) {
              this.formElements.city.value = this.formElements.city.options[0].original;
            }
          })
          .catch(e => {
            this.formElements.city.checking = false;
            this.formElements.city.error = e.errorI18NMessage;
          });
      } else {
        this.formElements.city.value = selectedCity;
      }
    }
  }

  validatePhoneNumber(number: string, value: VueTelInputModel) {
    this.phoneTelInfo = value;
    if (this.phoneTelInfo?.formatted !== '+' + this.phoneTelInfo?.country?.dialCode) {
      this.formElements.phone.value = value.formatted;
    } else {
      this.formElements.phone.value = undefined;
    }
  }

  isValidDkCpr(cpr: string) {
    // Danish cpr number regex
    //1 - 2 position indicates the person's birthday.
    //3 - 4 position indicates the person's month of birth.
    //5 - 6 position indicates the person's year of birth, without century.
    //7 - 10 position is a serial number.
    const cprRegex = new RegExp('^(0[1-9]|[12]\\d|3[01])(0[1-9]|1[0-2])\\d{2}[-]?\\d{4}$', 'gm');
    return cprRegex.test(cpr);
  }

  getDateFromCpr(cpr: string) {
    //5 - 6 position indicates the person's year of birth, without century.
    const cprDate = cpr.substring(0, 6).match(/.{1,2}/g);

    const month = parseInt(cprDate![1]) - 1;
    const day = parseInt(cprDate![0]);
    let date = new Date(Date.UTC(parseInt(cprDate![2]), month, day, 0, 0, 0, 0));
    const year = date.getFullYear();
    const centuryValue = cpr[6] === '-' ? parseInt(cpr[7]) : parseInt(cpr[6]);
    // 1900 or 2000
    if (centuryValue === 4 || centuryValue === 9) {
      if (year <= 1936) {
        date = new Date(Date.UTC(year + 100, month, day, 0, 0, 0, 0));
      }
      // 1800 or 2000
    } else if (centuryValue > 4) {
      date = new Date(Date.UTC(year > 1957 ? year - 100 : year + 100, month, day, 0, 0, 0, 0));
    }
    // TODO DEV-47286 Be carefull about this one. It might not always work. I checked on the main branch and it does fix the issue. But just be carefull
    return formatDateToUTC(date);
  }

  getGenderFromCpr(cpr: string) {
    // 10th position in the social security number indicates the person's gender.
    cpr = cpr.replace(/[^\d]/g, '');
    return parseInt(cpr) % 2 ? ApiGenderModel.Male : ApiGenderModel.Female;
  }

  checkCprField() {
    this.checkInput('ssn');
    if (this.formElements.ssn.value) {
      if (this.formConfig!.selectedCenter?.country == 'DK') {
        if (this.isValidDkCpr(this.formElements.ssn.value)) {
          this.formElements.birthday.value = this.getDateFromCpr(this.formElements.ssn.value);
          this.formElements.gender.value = this.getGenderFromCpr(this.formElements.ssn.value);
          this.checkInput('birthday');
          this.checkInput('gender');
          this.cprIsUsed = true;
        } else {
          this.formElements.ssn.error = 'subscription.invalidCpr';
        }
      }
    } else if (!this.formElements.ssn.value && this.formConfig!.selectedCenter?.country == 'DK') {
      this.cprIsUsed = false;
      this.formElements.birthday.value = undefined;
      this.formElements.gender.value = '';
      this.checkInput('ssn');
    }
  }

  initCentersCountry() {
    if (this.formConfig!.mode === 'PROFILE' && this.formConfig?.personDetail?.country) {
      this.vueTelProps.defaultCountry = this.formConfig?.personDetail?.country;
    } else if (this.formConfig?.selectedCenter?.country) {
      this.vueTelProps.defaultCountry = this.formConfig?.selectedCenter.country;
    }
  }

  resetCountryRelatedFields() {
    this.formElements.city.error = '';
    this.formElements.city.options = [];
    // this.formElements.zipCode.value = '';
    this.checkCities(true, undefined);
  }

  // TODO DEV-47286 I would like to have the logic here. I will try to fix it later
  // nothingToShow(): boolean {
  //   return (
  //     !this.theContainerHasChildren('personal-info-section') &&
  //     !this.theContainerHasChildren('contact-info-section') &&
  //     !this.theContainerHasChildren('address-section') &&
  //     !this.theContainerHasChildren('contract-section') &&
  //     !this.theContainerHasChildren('misc-section')
  //   );
  // }

  theContainerHasChildren(containerName: string): boolean {
    const container = this.$refs[containerName] as HTMLElement;
    if (container?.children?.length > 0) {
      return true;
    }
    return false;
  }

  applyPhoneInputDirection() {
    // was not able to update the direction in the vue-tel-input from HTML or CSS
    this.$nextTick(() => {
      const phoneInputRef = this.$refs.phoneInput as ComponentPublicInstance | undefined;
      const inputRoot = (phoneInputRef?.$el as HTMLElement | undefined) ?? null;
      const input = inputRoot?.querySelector('.vti__input') as HTMLInputElement | null;
      if (!input) {
        return;
      }

      input.setAttribute('dir', 'ltr'); // setting this to rtl will cause issue in rendering the phone number order
      input.style.textAlign = store.state.currentLocale?.direction === 'rtl' ? 'right' : 'left';
    });
  }

  validateForm(): ErrorModel | PersonDetailModel {
    this.globalError = null;
    for (const key in this.formElements) {
      this.checkInput(key, false);
    }

    const invalid = getInvalidFields(this.formElements);

    if (invalid) {
      const error: ErrorModel = {
        errorI18NMessage: 'subscription.checkFormAndTryAgain',
        errorCode: {
          extraDetails: [this.$t('subscription.invalidField', { fieldName: this.$t(`subscription.${invalid}`) })],
        },
        httpCode: 0,
      };
      return error;
    }

    const personDetails: PersonDetailModel = {} as PersonDetailModel;

    personDetails.firstName = this.formElements.firstName.value;
    personDetails.lastName = this.formElements.lastName.value;
    personDetails.birthday = this.formElements.birthday.value;
    personDetails.address1 = this.formElements.address1.value;
    personDetails.address2 = this.formElements.address2.value;
    personDetails.city = this.formElements.city.value;
    personDetails.zip = this.formElements.zipCode.value;
    personDetails.ssn = this.formElements.ssn.value;
    personDetails.state = this.formElements.state.value;
    personDetails.country = this.formElements.country.value;
    personDetails.gender = this.formElements.gender.value;
    personDetails.mobile = this.formElements.phone.value;
    personDetails.salutation = (this.formElements.salutation.value as SalutationModel)?.value;
    personDetails.preferredLanguage = this.formElements.language.value;
    // TODO DEV-47286 This means that this attributes can only have two values, true or false. When never checked it will contain the value false
    personDetails.allowThirdPartyOffers = this.formElements.allowThirdPartyOffers.value || false;
    personDetails.allowEmailNewsletters = this.formElements.allowEmailNewsletters.value || false;

    personDetails.nationalId = this.formElements.nationalId.value;

    if (this.formConfig.mode === 'JOIN' || this.formConfig.mode === 'LEAD') {
      if (this.formElements.idType.value === 'passport') {
        personDetails.passportNumber = this.formElements.idNumber.value;
        personDetails.passportIssuingCountry = this.formElements.passportCountry.value;
        personDetails.passportExpiration = this.formElements.passportDate.value;
        personDetails.residentId = undefined;
        personDetails.nationalId = undefined;
      } else if (this.formElements.idType.value === 'residentId') {
        personDetails.residentId = this.formElements.idNumber.value;
        personDetails.passportNumber = undefined;
        personDetails.passportIssuingCountry = undefined;
        personDetails.passportExpiration = undefined;
        personDetails.nationalId = undefined;
      } else {
        personDetails.nationalId = this.formElements.idNumber.value;
        personDetails.passportNumber = undefined;
        personDetails.passportIssuingCountry = undefined;
        personDetails.passportExpiration = undefined;
        personDetails.residentId = undefined;
      }
    } else {
      personDetails.nationalId = this.formElements.nationalId.value;
      personDetails.residentId = this.formElements.residentId.value;
      personDetails.passportNumber = this.formElements.passport.value;
      personDetails.passportIssuingCountry = this.formElements.passportCountry.value;
      personDetails.passportExpiration = this.formElements.passportDate.value;
    }

    return personDetails;
  }

  shouldBeVisible(fieldConfig: VIEW_NOT_USED_OPTIONAL_REQUIRED | undefined, formField: FormField): boolean {
    return fieldConfig !== 'not-used' && !this.shouldBehiddenInEditMode(fieldConfig, formField);
  }

  shouldBehiddenInEditMode(fieldConfig: VIEW_NOT_USED_OPTIONAL_REQUIRED | undefined, formField: FormField): boolean {
    if (this.formConfig?.viewMode === 'EDIT') {
      return fieldConfig === 'view' && !formField.value;
    }
    return false;
  }

  showPassportDetails(): boolean {
    if (this.formConfig?.mode === 'JOIN' || this.formConfig?.mode === 'LEAD') {
      return this.formElements.idType.value === 'passport' && this.formConfig.systemPropertyConfig.passport !== 'not-used';
    } else {
      return this.formConfig.systemPropertyConfig.passport !== 'not-used' && this.formElements.passport.value;
    }
  }
}
